/**
 * Feature gate for the Personal/Team workspaces UI (switcher + team creator).
 *
 * ON everywhere by default, production included. The only way to turn it off
 * is an explicit VITE_WORKSPACES_ENABLED=false at build time — a build with
 * the variable missing must never hide workspaces from paying teams.
 *
 * When this returns false:
 *   - the top bar hides the workspace switcher (with "Manage team" and
 *     "Create a team");
 *   - the Team sidebar nav entry is hidden;
 *   - useWorkspaceContext / getActiveDesignFirmId force-resolve to Personal,
 *     so every consumer behaves exactly as it did pre-workspaces (new content
 *     is stamped with design_firm_id = null and lists show Personal content).
 *
 * Read at call-time (not module-load) so the env can be stubbed per-suite in
 * tests.
 */
const readEnvOverride = (): string | undefined => {
  // Under vitest, read ONLY process.env: import.meta.env is populated
  // per-module from the developer's local .env files, and neither the test
  // setup scrub nor vi.stubEnv reaches it — so a local
  // VITE_WORKSPACES_ENABLED=true (set to try the workspaces UI on localhost)
  // would leak into the gated-mode tests. process.env is scrubbed in
  // creator's tests/setup.js and controlled per-test with vi.stubEnv.
  if (typeof process !== "undefined" && process.env?.VITEST) {
    return process.env.VITE_WORKSPACES_ENABLED;
  }
  // Vite app build exposes the var on import.meta.env; plain Node exposes it
  // on process.env. Check both — `process` is guarded since it's undefined
  // in the browser bundle.
  let value: string | undefined;
  try {
    value = (import.meta as any).env.VITE_WORKSPACES_ENABLED;
  } catch {
    /* import.meta.env unavailable in this context (e.g. plain Node) */
  }
  if (value === undefined && typeof process !== "undefined") {
    value = process.env?.VITE_WORKSPACES_ENABLED;
  }
  return value;
};

export const workspacesEnabled = (): boolean => readEnvOverride() !== "false";
